// Shared UI primitive (reused across all dashboard pages) — MIS-809.
//
// A tiny toast system — deliberately NOT `sonner` (no new dependency / no
// lockfile change). `ToastProvider` (mounted once in main.tsx) holds a small
// queue and renders a fixed bottom-right stack; `useToast()` returns a `toast()`
// fn that pushes a notification which auto-dismisses after `duration` ms (default
// 5s) or on click. Variants color the left border (default/success/destructive).

import { createContext, useCallback, useContext, useRef, useState } from 'react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'default' | 'success' | 'destructive';

export interface ToastOptions {
  title: string;
  description?: string;
  variant?: ToastVariant;
  /** Auto-dismiss delay in ms. Defaults to 5000. */
  duration?: number;
}

interface ToastRecord extends ToastOptions {
  id: number;
}

interface ToastContextValue {
  toast: (options: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

/**
 * Access the `toast()` function. Throws if used outside {@link ToastProvider} so
 * a missing provider fails loudly rather than silently dropping notifications.
 */
export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error('useToast must be called inside ToastProvider');
  }
  return ctx;
}

const VARIANT_BORDER: Record<ToastVariant, string> = {
  default: 'border-l-[var(--border-strong)]',
  success: 'border-l-[var(--accent)]',
  destructive: 'border-l-[var(--danger)]',
};

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<ToastRecord[]>([]);
  // Monotonic id source — a ref so successive toasts never collide on key.
  const nextId = useRef(0);

  const dismiss = useCallback((id: number): void => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions): void => {
      const id = nextId.current++;
      setToasts((prev) => [...prev, { ...options, id }]);
      const duration = options.duration ?? 5000;
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex w-full max-w-sm flex-col gap-2">
        {toasts.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => dismiss(t.id)}
            className={cn(
              'pointer-events-auto w-full rounded-md border border-l-4 border-[var(--border-soft)] bg-[var(--surface)] px-4 py-3 text-left shadow-lg',
              VARIANT_BORDER[t.variant ?? 'default'],
            )}
          >
            <div className="text-sm font-medium text-[var(--text)]">{t.title}</div>
            {t.description && (
              <div className="mt-0.5 text-sm text-[var(--muted)]">{t.description}</div>
            )}
          </button>
        ))}
      </div>
    </ToastContext.Provider>
  );
}
